// Task #705 — Ads Manager Phase 1.
//
// Phase-1 page is intentionally minimal: it surfaces every ads surface
// (accounts, campaigns, creatives, phone numbers, attribution touches,
// conversion pings) as flat tables so operators can verify the backend
// end-to-end. Richer charting/dashboards will land in Phase 2.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

type Campaign = {
  id: string;
  name: string;
  platform: string;
  status: string;
  dailyBudgetCents: number | null;
  utmDefaultCampaign: string | null;
  createdAt: string;
};

type AdAccount = {
  id: string;
  platform: string;
  externalAccountId: string;
  displayName: string | null;
  status: string;
  lastSyncedAt: string | null;
};

function formatMoney(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function MarketingAdsPage() {
  const { toast } = useToast();

  const accounts = useQuery<{ accounts: AdAccount[] }>({
    queryKey: ["/api/marketing/ads/accounts"],
  });
  const campaigns = useQuery<{ campaigns: Campaign[] }>({
    queryKey: ["/api/marketing/ads/campaigns"],
  });
  const touches = useQuery<{ touches: any[] }>({
    queryKey: ["/api/marketing/ads/touches"],
  });
  const pings = useQuery<{ pings: any[] }>({
    queryKey: ["/api/marketing/ads/pings"],
  });
  const phoneNumbers = useQuery<{ phoneNumbers: any[] }>({
    queryKey: ["/api/marketing/ads/phone-numbers"],
  });

  const startGoogleOAuth = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("GET", "/api/marketing/ads/oauth/google/start");
      return r.json();
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err: any) => {
      toast({
        title: "Google Ads not configured",
        description: err?.message ?? "Set ADS_GOOGLE_CLIENT_ID + ADS_GOOGLE_CLIENT_SECRET to enable.",
        variant: "destructive",
      });
    },
  });

  const startMetaOAuth = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("GET", "/api/marketing/ads/oauth/meta/start");
      return r.json();
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err: any) => {
      toast({
        title: "Meta Ads not configured",
        description: err?.message ?? "Set META_APP_ID + META_APP_SECRET to enable.",
        variant: "destructive",
      });
    },
  });

  // Task #706 — TikTok + LinkedIn OAuth start mutations.
  const startTiktokOAuth = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("GET", "/api/marketing/ads/oauth/tiktok/start");
      return r.json();
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err: any) => {
      toast({
        title: "TikTok Ads not configured",
        description: err?.message ?? "Set TIKTOK_CLIENT_KEY + TIKTOK_CLIENT_SECRET to enable.",
        variant: "destructive",
      });
    },
  });

  const startLinkedinOAuth = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("GET", "/api/marketing/ads/oauth/linkedin/start");
      return r.json();
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err: any) => {
      toast({
        title: "LinkedIn Ads not configured",
        description: err?.message ?? "Set LINKEDIN_CLIENT_ID + LINKEDIN_CLIENT_SECRET to enable.",
        variant: "destructive",
      });
    },
  });

  const [campaignForm, setCampaignForm] = useState({
    name: "",
    platform: "google",
    adAccountId: "",
    objective: "lead_gen",
    utmDefaultCampaign: "",
    dailyBudgetDollars: "",
  });

  const createCampaign = useMutation({
    mutationFn: async () => {
      const body = {
        name: campaignForm.name,
        platform: campaignForm.platform,
        adAccountId: campaignForm.adAccountId,
        objective: campaignForm.objective,
        utmDefaultCampaign: campaignForm.utmDefaultCampaign || null,
        dailyBudgetCents: campaignForm.dailyBudgetDollars
          ? Math.round(parseFloat(campaignForm.dailyBudgetDollars) * 100)
          : null,
        status: "draft",
      };
      const r = await apiRequest("POST", "/api/marketing/ads/campaigns", body);
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Campaign created" });
      setCampaignForm((s) => ({ ...s, name: "", utmDefaultCampaign: "", dailyBudgetDollars: "" }));
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/ads/campaigns"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not create campaign", description: err?.message, variant: "destructive" });
    },
  });

  const [creativeForm, setCreativeForm] = useState({
    product: "",
    audience: "",
    tone: "professional",
    callToAction: "Get a Quote",
    // Task #706 — platform shapes copy + image placement.
    platform: "google" as "google" | "meta" | "tiktok" | "linkedin",
  });
  const generateCreative = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", "/api/marketing/ads/creatives/generate", {
        ...creativeForm,
        save: true,
      });
      return r.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Creative generated",
        description: data?.generated?.headline ?? "Saved to creatives library.",
      });
    },
    onError: (err: any) => {
      toast({ title: "Generator unavailable", description: err?.message, variant: "destructive" });
    },
  });

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="page-marketing-ads">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-ads-title">Ad Manager</h1>
          <p className="text-sm text-muted-foreground">
            Manage Google, Meta, TikTok, and LinkedIn campaigns, creatives, tracking phone numbers, and attribution.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="button-connect-google"
            onClick={() => startGoogleOAuth.mutate()}
            disabled={startGoogleOAuth.isPending}
          >
            Connect Google Ads
          </Button>
          <Button
            data-testid="button-connect-meta"
            variant="secondary"
            onClick={() => startMetaOAuth.mutate()}
            disabled={startMetaOAuth.isPending}
          >
            Connect Meta Ads
          </Button>
          <Button
            data-testid="button-connect-tiktok"
            variant="secondary"
            onClick={() => startTiktokOAuth.mutate()}
            disabled={startTiktokOAuth.isPending}
          >
            Connect TikTok Ads
          </Button>
          <Button
            data-testid="button-connect-linkedin"
            variant="secondary"
            onClick={() => startLinkedinOAuth.mutate()}
            disabled={startLinkedinOAuth.isPending}
          >
            Connect LinkedIn Ads
          </Button>
        </div>
      </div>

      <Tabs defaultValue="campaigns">
        <TabsList>
          <TabsTrigger value="accounts" data-testid="tab-accounts">Accounts</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="creatives" data-testid="tab-creatives">Creatives</TabsTrigger>
          <TabsTrigger value="phones" data-testid="tab-phones">Tracking Numbers</TabsTrigger>
          <TabsTrigger value="touches" data-testid="tab-touches">Touches</TabsTrigger>
          <TabsTrigger value="pings" data-testid="tab-pings">Conversion Pings</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="accounts">
          <Card>
            <CardHeader><CardTitle>Connected Ad Accounts</CardTitle></CardHeader>
            <CardContent>
              {accounts.isLoading && <p>Loading…</p>}
              {!accounts.isLoading && (accounts.data?.accounts.length ?? 0) === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="text-no-accounts">
                  No accounts connected yet. Use the buttons above once OAuth credentials are configured.
                </p>
              )}
              <div className="space-y-2">
                {accounts.data?.accounts.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded border p-3"
                       data-testid={`row-account-${a.id}`}>
                    <div>
                      <div className="font-medium">{a.displayName ?? a.externalAccountId}</div>
                      <div className="text-xs text-muted-foreground">
                        {a.platform.toUpperCase()} · {a.externalAccountId}
                      </div>
                    </div>
                    <Badge>{a.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4">
          <Card>
            <CardHeader><CardTitle>New Campaign</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="campaign-name">Name</Label>
                <Input id="campaign-name" data-testid="input-campaign-name"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm((s) => ({ ...s, name: e.target.value }))}/>
              </div>
              <div>
                <Label htmlFor="campaign-platform">Platform</Label>
                <select
                  id="campaign-platform"
                  data-testid="select-campaign-platform"
                  className="w-full border rounded p-2 bg-background"
                  value={campaignForm.platform}
                  onChange={(e) => setCampaignForm((s) => ({ ...s, platform: e.target.value, adAccountId: "" }))}>
                  <option value="google">Google Ads</option>
                  <option value="meta">Meta Ads</option>
                  <option value="tiktok">TikTok Ads</option>
                  <option value="linkedin">LinkedIn Ads</option>
                </select>
              </div>
              <div>
                <Label htmlFor="campaign-account">Ad Account</Label>
                <select
                  id="campaign-account"
                  data-testid="select-campaign-account"
                  className="w-full border rounded p-2 bg-background"
                  value={campaignForm.adAccountId}
                  onChange={(e) => setCampaignForm((s) => ({ ...s, adAccountId: e.target.value }))}>
                  <option value="">Select an account…</option>
                  {accounts.data?.accounts
                    .filter((a) => a.platform === campaignForm.platform)
                    .map((a) => (
                      <option key={a.id} value={a.id}>{a.platform}: {a.displayName ?? a.externalAccountId}</option>
                    ))}
                </select>
              </div>
              <div>
                <Label htmlFor="campaign-utm">UTM Campaign</Label>
                <Input id="campaign-utm" data-testid="input-campaign-utm"
                  value={campaignForm.utmDefaultCampaign}
                  onChange={(e) => setCampaignForm((s) => ({ ...s, utmDefaultCampaign: e.target.value }))}/>
              </div>
              <div>
                <Label htmlFor="campaign-budget">Daily Budget ($)</Label>
                <Input id="campaign-budget" type="number" step="0.01" data-testid="input-campaign-budget"
                  value={campaignForm.dailyBudgetDollars}
                  onChange={(e) => setCampaignForm((s) => ({ ...s, dailyBudgetDollars: e.target.value }))}/>
              </div>
              <div className="flex items-end">
                <Button
                  data-testid="button-create-campaign"
                  onClick={() => createCampaign.mutate()}
                  disabled={createCampaign.isPending || !campaignForm.name || !campaignForm.adAccountId}>
                  Create Campaign
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Campaigns</CardTitle></CardHeader>
            <CardContent>
              {campaigns.isLoading && <p>Loading…</p>}
              <div className="space-y-2">
                {campaigns.data?.campaigns.map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded border p-3"
                       data-testid={`row-campaign-${c.id}`}>
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.platform.toUpperCase()} · UTM: {c.utmDefaultCampaign ?? "—"} · Daily: {formatMoney(c.dailyBudgetCents)}
                      </div>
                    </div>
                    <Badge>{c.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="creatives">
          <Card>
            <CardHeader><CardTitle>AI Creative Generator</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="creative-product">Product / Service</Label>
                <Input id="creative-product" data-testid="input-creative-product"
                  value={creativeForm.product}
                  onChange={(e) => setCreativeForm((s) => ({ ...s, product: e.target.value }))}/>
              </div>
              <div>
                <Label htmlFor="creative-audience">Audience</Label>
                <Input id="creative-audience" data-testid="input-creative-audience"
                  value={creativeForm.audience}
                  onChange={(e) => setCreativeForm((s) => ({ ...s, audience: e.target.value }))}/>
              </div>
              <div>
                <Label htmlFor="creative-tone">Tone</Label>
                <Input id="creative-tone" data-testid="input-creative-tone"
                  value={creativeForm.tone}
                  onChange={(e) => setCreativeForm((s) => ({ ...s, tone: e.target.value }))}/>
              </div>
              <div>
                <Label htmlFor="creative-cta">Call to Action</Label>
                <Input id="creative-cta" data-testid="input-creative-cta"
                  value={creativeForm.callToAction}
                  onChange={(e) => setCreativeForm((s) => ({ ...s, callToAction: e.target.value }))}/>
              </div>
              <div>
                <Label htmlFor="creative-platform">Platform</Label>
                <select
                  id="creative-platform"
                  data-testid="select-creative-platform"
                  className="w-full border rounded p-2 bg-background"
                  value={creativeForm.platform}
                  onChange={(e) => setCreativeForm((s) => ({ ...s, platform: e.target.value as typeof s.platform }))}>
                  <option value="google">Google Ads (1.91:1 landscape)</option>
                  <option value="meta">Meta (1:1 square)</option>
                  <option value="tiktok">TikTok (9:16 vertical)</option>
                  <option value="linkedin">LinkedIn (1.91:1 landscape, B2B)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Button
                  data-testid="button-generate-creative"
                  onClick={() => generateCreative.mutate()}
                  disabled={generateCreative.isPending || !creativeForm.product || !creativeForm.audience}>
                  Generate Creative
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="phones">
          <Card>
            <CardHeader><CardTitle>Tracking Phone Numbers</CardTitle></CardHeader>
            <CardContent>
              {(phoneNumbers.data?.phoneNumbers ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="text-no-phones">
                  No tracking numbers yet. Provision a Twilio number and POST it to /api/marketing/ads/phone-numbers
                  with `{`{ campaignId, phoneNumber, forwardTo }`}`.
                </p>
              )}
              <div className="space-y-2">
                {phoneNumbers.data?.phoneNumbers.map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between rounded border p-3"
                       data-testid={`row-phone-${p.id}`}>
                    <div>
                      <div className="font-medium">{p.phoneNumber}</div>
                      <div className="text-xs text-muted-foreground">→ {p.forwardTo ?? "no forward"}</div>
                    </div>
                    <Badge>{p.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="touches">
          <Card>
            <CardHeader><CardTitle>Recent Attribution Touches</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-auto max-h-[600px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="text-left border-b">
                      <th className="p-2">When</th>
                      <th className="p-2">UTM Source / Medium / Campaign</th>
                      <th className="p-2">gclid / fbclid</th>
                      <th className="p-2">Credited</th>
                    </tr>
                  </thead>
                  <tbody>
                    {touches.data?.touches.map((t: any) => (
                      <tr key={t.id} className="border-b" data-testid={`row-touch-${t.id}`}>
                        <td className="p-2">{new Date(t.occurredAt).toLocaleString()}</td>
                        <td className="p-2">{t.utmSource ?? "—"} / {t.utmMedium ?? "—"} / {t.utmCampaign ?? "—"}</td>
                        <td className="p-2 font-mono text-xs">{t.gclid ?? t.fbclid ?? "—"}</td>
                        <td className="p-2">
                          {t.creditedInvoiceId ? <Badge>invoice</Badge>
                            : t.creditedClientId ? <Badge>client</Badge>
                            : t.creditedLeadId ? <Badge>lead</Badge>
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pings">
          <Card>
            <CardHeader><CardTitle>Conversions API Audit Log</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-auto max-h-[600px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="text-left border-b">
                      <th className="p-2">When</th>
                      <th className="p-2">Platform</th>
                      <th className="p-2">Event</th>
                      <th className="p-2">Source</th>
                      <th className="p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pings.data?.pings.map((p: any) => (
                      <tr key={p.id} className="border-b" data-testid={`row-ping-${p.id}`}>
                        <td className="p-2">{new Date(p.enqueuedAt).toLocaleString()}</td>
                        <td className="p-2">{p.platform}</td>
                        <td className="p-2">{p.eventName}</td>
                        <td className="p-2">{p.sourceKind} / {p.sourceId.slice(0, 8)}</td>
                        <td className="p-2">
                          <Badge variant={p.status === "sent" ? "default" : p.status === "failed" ? "destructive" : "secondary"}>
                            {p.status}
                          </Badge>
                          {p.lastError && <div className="text-xs text-muted-foreground">{p.lastError}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <PerformanceTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Task #707 — Performance / attribution dashboard ───────────────────
type AttributionModel = "first" | "last" | "linear";

type PerfSummary = {
  dateFrom: string;
  dateTo: string;
  model: AttributionModel;
  totals: { spendCents: number; leads: number; bookings: number; revenueCents: number;
    roas: number; cplCents: number; cpbCents: number };
  byPlatform: Array<{ platform: string; spendCents: number; leads: number;
    bookings: number; revenueCents: number; roas: number }>;
  byCampaign: Array<{ campaignId: string | null; campaignName: string; platform: string;
    spendCents: number; clicks: number; impressions: number; leads: number; bookings: number;
    revenueCents: number; roas: number; cplCents: number; cpbCents: number }>;
  byArtist: Array<{ talentId: string | null; talentName: string;
    leads: number; bookings: number; revenueCents: number }>;
  topPerformers: PerfSummary["byCampaign"];
  bottomPerformers: PerfSummary["byCampaign"];
};

type FunnelResp = { impressions: number; clicks: number; leads: number;
  qualifiedLeads: number; bookings: number; revenueCents: number;
  dateFrom: string; dateTo: string };

type DrillDown = {
  campaign: { id: string; name: string; platform: string };
  campaignId: string;
  creatives: Array<{ id: string; kind: string; headline: string | null;
    body: string | null; callToAction: string | null; imageUrl: string | null }>;
  linkedLeads: Array<{ id: string; name: string | null; email: string | null;
    status: string | null; ratingLabel: string | null; touchedAt: string | null }>;
  linkedClients: Array<{ id: string; name: string | null; touchedAt: string | null }>;
  linkedInvoices: Array<{ id: string; invoiceNumber: string | null;
    status: string | null; totalCents: number; touchedAt: string | null }>;
};

type SortKey = "campaignName" | "platform" | "spendCents" | "leads"
  | "bookings" | "revenueCents" | "roas" | "cplCents" | "cpbCents";

function defaultRange() {
  const today = new Date();
  const from = new Date(today);
  from.setUTCDate(from.getUTCDate() - 30);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(today) };
}

function PerformanceTab() {
  const init = defaultRange();
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [model, setModel] = useState<AttributionModel>("last");
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("revenueCents");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
  const qs = `?from=${from}&to=${to}&model=${model}`;

  // Workspace-level default attribution model (read on mount, persisted
  // via PATCH below). The page-local `model` state seeds from this on
  // first load so the dashboard shows the admin's chosen default.
  const settings = useQuery<{ attributionDefault: AttributionModel }>({
    queryKey: ["/api/marketing/ads/performance/settings"],
    queryFn: async () => {
      const r = await apiRequest("GET", "/api/marketing/ads/performance/settings");
      return r.json();
    },
  });
  const seededRef = useRef(false);
  useEffect(() => {
    if (settings.data && !seededRef.current) {
      seededRef.current = true;
      setModel(settings.data.attributionDefault);
    }
  }, [settings.data]);

  const updateDefault = useMutation({
    mutationFn: async (next: AttributionModel) => {
      const r = await apiRequest("PATCH", "/api/marketing/ads/performance/settings",
        { attributionDefault: next });
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/ads/performance/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketing/ads/performance/summary"] });
    },
  });

  const summary = useQuery<PerfSummary>({
    queryKey: ["/api/marketing/ads/performance/summary", from, to, model],
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/marketing/ads/performance/summary${qs}`);
      return r.json();
    },
  });
  const funnel = useQuery<FunnelResp>({
    queryKey: ["/api/marketing/ads/performance/funnel", from, to],
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/marketing/ads/performance/funnel?from=${from}&to=${to}`);
      return r.json();
    },
  });
  const drill = useQuery<DrillDown>({
    queryKey: ["/api/marketing/ads/performance/campaign", expandedCampaign, from, to, model],
    enabled: !!expandedCampaign,
    queryFn: async () => {
      const r = await apiRequest("GET",
        `/api/marketing/ads/performance/campaign/${expandedCampaign}/drill?from=${from}&to=${to}&model=${model}`);
      return r.json();
    },
  });

  const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const fmtRoas = (n: number) => (n === 0 ? "—" : `${n.toFixed(2)}×`);

  // Apply filter + sort to the campaign rows. Sorting is purely
  // client-side so it doesn't burn another network round-trip; the
  // server already orders by revenue so the default is meaningful.
  const visibleCampaigns = useMemo(() => {
    if (!summary.data) return [];
    let rows = summary.data.byCampaign.slice();
    if (platformFilter !== "all") rows = rows.filter(r => r.platform === platformFilter);
    rows.sort((a, b) => {
      const av = a[sortKey] as any;
      const bv = b[sortKey] as any;
      const cmp = typeof av === "string" ? av.localeCompare(bv) : (Number(av) - Number(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [summary.data, platformFilter, sortKey, sortDir]);

  const onSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "campaignName" || k === "platform" ? "asc" : "desc"); }
  };
  const sortArrow = (k: SortKey) => sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Date range & attribution model</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="perf-from">From</Label>
            <Input id="perf-from" type="date" data-testid="input-perf-from"
              value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="perf-to">To</Label>
            <Input id="perf-to" type="date" data-testid="input-perf-to"
              value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="perf-model">Attribution model</Label>
            <select id="perf-model" data-testid="select-perf-model"
              className="w-full border rounded p-2 bg-background"
              value={model}
              onChange={(e) => setModel(e.target.value as AttributionModel)}>
              <option value="last">Last touch</option>
              <option value="first">First touch</option>
              <option value="linear">Linear (split equally)</option>
            </select>
          </div>
          <div className="flex items-end">
            <a
              data-testid="link-export-csv"
              href={`/api/marketing/ads/performance/export.csv${qs}`}
              className="inline-flex items-center px-3 py-2 rounded bg-primary text-primary-foreground text-sm"
            >
              Export CSV
            </a>
          </div>
        </CardContent>
        <CardContent className="border-t pt-4 flex items-center gap-3">
          <Label htmlFor="perf-default-model" className="text-sm">
            Workspace default model:
          </Label>
          <select id="perf-default-model" data-testid="select-perf-default-model"
            className="border rounded p-1 bg-background text-sm"
            value={settings.data?.attributionDefault ?? "last"}
            onChange={(e) => updateDefault.mutate(e.target.value as AttributionModel)}
            disabled={updateDefault.isPending}>
            <option value="last">Last touch</option>
            <option value="first">First touch</option>
            <option value="linear">Linear</option>
          </select>
          <span className="text-xs text-muted-foreground">
            Persists across sessions; overridden by the per-view selector above.
          </span>
        </CardContent>
      </Card>

      {summary.isLoading && <p data-testid="text-perf-loading">Loading performance…</p>}
      {summary.isError && (
        <p className="text-destructive" data-testid="text-perf-error">
          Couldn't load report — {(summary.error as any)?.message ?? "try again"}
        </p>
      )}

      {summary.data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Spend" value={fmtMoney(summary.data.totals.spendCents)} testid="kpi-spend" />
            <KpiCard label="Leads" value={String(summary.data.totals.leads)} testid="kpi-leads" />
            <KpiCard label="Bookings" value={String(summary.data.totals.bookings)} testid="kpi-bookings" />
            <KpiCard label="Revenue" value={fmtMoney(summary.data.totals.revenueCents)} testid="kpi-revenue" />
            <KpiCard label="ROAS" value={fmtRoas(summary.data.totals.roas)} testid="kpi-roas" />
            <KpiCard label="CPL" value={fmtMoney(summary.data.totals.cplCents)} testid="kpi-cpl" />
            <KpiCard label="CPB" value={fmtMoney(summary.data.totals.cpbCents)} testid="kpi-cpb" />
          </div>

          {funnel.data && (
            <Card>
              <CardHeader><CardTitle>Funnel</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <FunnelStep label="Impressions" value={funnel.data.impressions} testid="funnel-impressions" />
                  <FunnelStep label="Clicks" value={funnel.data.clicks} testid="funnel-clicks" />
                  <FunnelStep label="Leads" value={funnel.data.leads} testid="funnel-leads" />
                  <FunnelStep label="Qualified Leads" value={funnel.data.qualifiedLeads} testid="funnel-qualified-leads" />
                  <FunnelStep label="Bookings" value={funnel.data.bookings} testid="funnel-bookings" />
                  <FunnelStep label="Revenue" value={fmtMoney(funnel.data.revenueCents)} testid="funnel-revenue" />
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>By Platform</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm" data-testid="table-by-platform">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr><th className="p-2">Platform</th><th className="p-2">Spend</th>
                      <th className="p-2">Leads</th><th className="p-2">Bookings</th>
                      <th className="p-2">Revenue</th><th className="p-2">ROAS</th></tr>
                  </thead>
                  <tbody>
                    {summary.data.byPlatform.map((p) => (
                      <tr key={p.platform} className="border-t" data-testid={`row-platform-${p.platform}`}>
                        <td className="p-2 font-medium">{p.platform.toUpperCase()}</td>
                        <td className="p-2">{fmtMoney(p.spendCents)}</td>
                        <td className="p-2">{p.leads}</td>
                        <td className="p-2">{p.bookings}</td>
                        <td className="p-2">{fmtMoney(p.revenueCents)}</td>
                        <td className="p-2">{fmtRoas(p.roas)}</td>
                      </tr>
                    ))}
                    {summary.data.byPlatform.length === 0 && (
                      <tr><td className="p-2 text-muted-foreground" colSpan={6}>No spend or credited conversions in this range.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>By Artist</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm" data-testid="table-by-artist">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr><th className="p-2">Artist</th><th className="p-2">Leads</th>
                      <th className="p-2">Bookings</th><th className="p-2">Revenue</th></tr>
                  </thead>
                  <tbody>
                    {summary.data.byArtist.map((a) => (
                      <tr key={a.talentId ?? "unassigned"} className="border-t"
                          data-testid={`row-artist-${a.talentId ?? "unassigned"}`}>
                        <td className="p-2 font-medium">{a.talentName}</td>
                        <td className="p-2">{a.leads}</td>
                        <td className="p-2">{a.bookings}</td>
                        <td className="p-2">{fmtMoney(a.revenueCents)}</td>
                      </tr>
                    ))}
                    {summary.data.byArtist.length === 0 && (
                      <tr><td className="p-2 text-muted-foreground" colSpan={4}>No credited conversions yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>By Campaign</CardTitle>
              <div className="flex items-center gap-2 pt-2">
                <Label htmlFor="perf-platform-filter" className="text-xs">Platform filter:</Label>
                <select id="perf-platform-filter" data-testid="select-platform-filter"
                  className="border rounded p-1 bg-background text-sm"
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}>
                  <option value="all">All</option>
                  {summary.data.byPlatform.map(p => (
                    <option key={p.platform} value={p.platform}>{p.platform.toUpperCase()}</option>
                  ))}
                </select>
                <span className="text-xs text-muted-foreground">
                  Click any column header to sort. Click a row to see creatives + linked entities.
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto max-h-[600px]">
                <table className="w-full text-sm" data-testid="table-by-campaign">
                  <thead className="sticky top-0 bg-background text-left text-xs text-muted-foreground">
                    <tr>
                      <SortHeader k="campaignName" label="Campaign" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <SortHeader k="platform" label="Platform" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <SortHeader k="spendCents" label="Spend" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <SortHeader k="leads" label="Leads" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <SortHeader k="bookings" label="Bookings" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <SortHeader k="revenueCents" label="Revenue" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <SortHeader k="roas" label="ROAS" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <SortHeader k="cplCents" label="CPL" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <SortHeader k="cpbCents" label="CPB" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCampaigns.map((r) => {
                      const cid = r.campaignId ?? "none";
                      const isOpen = expandedCampaign === r.campaignId;
                      return (
                        <FragmentRow key={`${cid}-${r.platform}`}>
                          <tr className="border-t hover:bg-muted/40 cursor-pointer"
                              data-testid={`row-campaign-perf-${cid}`}
                              onClick={() => r.campaignId && setExpandedCampaign(isOpen ? null : r.campaignId)}>
                            <td className="p-2">
                              {r.campaignId && (
                                <button className="mr-1 text-xs"
                                  data-testid={`button-drill-${cid}`}
                                  aria-label={isOpen ? "Collapse drill-down" : "Expand drill-down"}>
                                  {isOpen ? "▼" : "▶"}
                                </button>
                              )}
                              {r.campaignName}
                            </td>
                            <td className="p-2">{r.platform.toUpperCase()}</td>
                            <td className="p-2">{fmtMoney(r.spendCents)}</td>
                            <td className="p-2">{r.leads}</td>
                            <td className="p-2">{r.bookings}</td>
                            <td className="p-2">{fmtMoney(r.revenueCents)}</td>
                            <td className="p-2">{fmtRoas(r.roas)}</td>
                            <td className="p-2">{fmtMoney(r.cplCents)}</td>
                            <td className="p-2">{fmtMoney(r.cpbCents)}</td>
                          </tr>
                          {isOpen && (
                            <tr className="border-t bg-muted/20">
                              <td className="p-3" colSpan={9}>
                                {drill.isLoading && <p data-testid="text-drill-loading">Loading drill-down…</p>}
                                {drill.data && drill.data.campaignId === r.campaignId && (
                                  <DrillView drill={drill.data} fmtMoney={fmtMoney} />
                                )}
                              </td>
                            </tr>
                          )}
                        </FragmentRow>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>Top performers (by ROAS)</CardTitle></CardHeader>
              <CardContent>
                <PerformerList rows={summary.data.topPerformers} testid="list-top-performers" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Bottom performers (by ROAS)</CardTitle></CardHeader>
              <CardContent>
                <PerformerList rows={summary.data.bottomPerformers} testid="list-bottom-performers" />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, testid }: { label: string; value: string; testid: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold" data-testid={testid}>{value}</div>
      </CardContent>
    </Card>
  );
}

function FunnelStep({ label, value, testid }: { label: string; value: number; testid: string }) {
  return (
    <div className="rounded border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold" data-testid={testid}>{value}</div>
    </div>
  );
}

function SortHeader({ k, label, sortKey, sortDir, onSort }:
  { k: SortKey; label: string; sortKey: SortKey; sortDir: "asc" | "desc"; onSort: (k: SortKey) => void }) {
  const active = sortKey === k;
  return (
    <th className="p-2 cursor-pointer select-none"
        data-testid={`th-sort-${k}`}
        onClick={() => onSort(k)}>
      {label}{active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
}

function FragmentRow({ children }: { children: React.ReactNode }) {
  // Inline replacement for React.Fragment so we can attach key on parent.
  return <>{children}</>;
}

function DrillView({ drill, fmtMoney }: { drill: DrillDown; fmtMoney: (c: number) => string }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid={`drill-${drill.campaignId}`}>
      <div>
        <h4 className="text-sm font-semibold mb-2">Creatives ({drill.creatives.length})</h4>
        {drill.creatives.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="drill-creatives-empty">No creatives on this campaign.</p>
        ) : (
          <ul className="space-y-2" data-testid="drill-creatives">
            {drill.creatives.map(c => (
              <li key={c.id} className="rounded border p-2 flex gap-2"
                  data-testid={`drill-creative-${c.id}`}>
                {c.imageUrl && (
                  <img src={c.imageUrl} alt="" className="h-12 w-12 object-cover rounded" />
                )}
                <div className="text-xs">
                  <div className="font-medium">{c.headline ?? "(no headline)"}</div>
                  <div className="text-muted-foreground">{c.body ?? ""}</div>
                  {c.callToAction && <Badge className="mt-1">{c.callToAction}</Badge>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="space-y-3">
        <DrillSection
          title={`Credited Leads (${drill.linkedLeads.length})`}
          testid="drill-leads"
          rows={drill.linkedLeads.map(l => ({
            id: l.id, label: l.name ?? l.email ?? "(no name)",
            sub: `${l.status ?? ""}${l.ratingLabel ? " · " + l.ratingLabel : ""}`,
            href: `/leads/${l.id}`,
          }))}
        />
        <DrillSection
          title={`Credited Clients (${drill.linkedClients.length})`}
          testid="drill-clients"
          rows={drill.linkedClients.map(c => ({
            id: c.id, label: c.name ?? "(no name)", sub: "",
            href: `/clients/${c.id}`,
          }))}
        />
        <DrillSection
          title={`Credited Bookings (${drill.linkedInvoices.length})`}
          testid="drill-invoices"
          rows={drill.linkedInvoices.map(i => ({
            id: i.id, label: i.invoiceNumber ?? i.id.slice(0, 8),
            sub: `${i.status ?? ""} · ${fmtMoney(i.totalCents)}`,
            href: `/invoices/${i.id}`,
          }))}
        />
      </div>
    </div>
  );
}

function DrillSection({ title, testid, rows }:
  { title: string; testid: string;
    rows: Array<{ id: string; label: string; sub: string; href: string }> }) {
  return (
    <div>
      <h4 className="text-sm font-semibold mb-1">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid={`${testid}-empty`}>None in this date range.</p>
      ) : (
        <ul className="space-y-1" data-testid={testid}>
          {rows.slice(0, 10).map(r => (
            <li key={r.id} className="text-xs flex justify-between rounded border p-1"
                data-testid={`${testid}-row-${r.id}`}>
              <a href={r.href} className="font-medium hover:underline">{r.label}</a>
              <span className="text-muted-foreground">{r.sub}</span>
            </li>
          ))}
          {rows.length > 10 && (
            <li className="text-xs text-muted-foreground">…and {rows.length - 10} more</li>
          )}
        </ul>
      )}
    </div>
  );
}

function PerformerList({ rows, testid }: { rows: PerfSummary["byCampaign"]; testid: string }) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground" data-testid={`${testid}-empty`}>No campaigns with spend in this range.</p>;
  }
  return (
    <ul className="space-y-2" data-testid={testid}>
      {rows.map((r) => (
        <li key={`${r.campaignId}-${r.platform}`} className="flex items-center justify-between rounded border p-2">
          <div>
            <div className="font-medium text-sm">{r.campaignName}</div>
            <div className="text-xs text-muted-foreground">{r.platform.toUpperCase()}</div>
          </div>
          <Badge>{r.roas === 0 ? "—" : `${r.roas.toFixed(2)}×`}</Badge>
        </li>
      ))}
    </ul>
  );
}
